<template>
  <div class="modal-mask">
    <div class="modal-wrapper">
      <div class="modal-container">
        <div class="modal-header">
          <slot name="header">
            <img src="../assets/images/printer.svg" alt="printer" class="printer-icon" :class="{ 'generating': phase === 'generating' }" />
          </slot>
        </div>
        <div class="modal-body">
          <h3>{{ heading }}</h3>
          
          <!-- Generating phase - no progress bar, just spinner -->
          <div v-if="phase === 'generating'" class="generating-phase">
            <div class="spinner">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
            <p class="status-text">{{ statusText }}</p>
          </div>

          <!-- Printing phase - show progress bar -->
          <div v-else>
            <!-- Progress bar -->
            <div class="progress-container">
              <div class="progress-bar">
                <div 
                  class="progress-fill" 
                  :style="{ width: progressPercentage + '%' }"
                  :class="{ 'has-errors': failures > 0 }"
                ></div>
              </div>
              <div class="progress-text">
                {{ current }} / {{ total }}
              </div>
            </div>

            <!-- Current status -->
            <p class="status-text">{{ statusText }}</p>

            <!-- Success/Failure counters -->
            <div class="counters" v-if="current > 0">
              <div class="counter success" v-if="successes > 0">
                <span class="counter-icon">&#10003;</span>
                <span class="counter-text">{{ successes }} printed</span>
              </div>
              <div class="counter failure" v-if="failures > 0">
                <span class="counter-icon">&#10007;</span>
                <span class="counter-value">{{ failures }}</span>
                <span class="counter-label">failed</span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PrintingProgress",
  props: {
    heading: {
      type: String,
      default: "Printing"
    },
    phase: {
      type: String,
      default: "printing" // 'generating' or 'printing'
    },
    total: {
      type: Number,
      default: 0
    },
    current: {
      type: Number,
      default: 0
    },
    successes: {
      type: Number,
      default: 0
    },
    failures: {
      type: Number,
      default: 0
    },
    currentLabel: {
      type: String,
      default: ""
    }
  },
  computed: {
    progressPercentage() {
      if (this.total === 0) return 0;
      return Math.round((this.current / this.total) * 100);
    },
    statusText() {
      if (this.phase === 'generating') {
        return "Generating print job(s)...";
      }
      if (this.current === 0) {
        return "Preparing to send to printer...";
      }
      if (this.current === this.total) {
        return "Finalizing...";
      }
      if (this.currentLabel) {
        return `Sending to printer: ${this.currentLabel}`;
      }
      return `Sending label ${this.current} of ${this.total} to printer...`;
    }
  }
};
</script>

<style lang="scss" scoped>
.modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0;
  margin: 0;
  z-index: 1200;
  background: rgba(192, 221, 255, 0.95);
}

.modal-wrapper {
  position: absolute;
  transform: translate(-50%, -50%);
  top: 50%;
  left: 50%;
}

.modal-container {
  box-shadow: 0 0 50px rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  background: #fff;
  color: #0b3959;
  width: 480px;
  min-height: 320px;
}

.modal-header {
  height: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #255a80;
  border-radius: 8px 8px 0 0;
  
  .printer-icon {
    width: 60px;
    height: 60px;
    animation: pulse 1.5s ease-in-out infinite;
    
    &.generating {
      animation: pulse 1s ease-in-out infinite;
      opacity: 0.7;
    }
  }
}

.modal-body {
  text-align: center;
  padding: 24px 36px;
  
  h3 {
    color: #0b3959;
    font-size: 24px;
    margin-top: 0;
    margin-bottom: 20px;
  }
}

.progress-container {
  margin-bottom: 16px;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background: #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #255a80, #3d8bc9);
  border-radius: 6px;
  transition: width 0.3s ease;
  
  &.has-errors {
    background: linear-gradient(90deg, #255a80, #f5a623);
  }
}

.progress-text {
  font-size: 14px;
  color: #666;
  font-weight: 600;
}

.status-text {
  font-size: 14px;
  color: #888;
  margin: 12px 0;
  min-height: 20px;
}

.counters {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 16px;
}

.counter {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  
  &.success {
    background: #f5f5f5;
    color: #666;
    
    .counter-icon {
      color: #888;
      font-weight: bold;
    }
  }
  
  &.failure {
    background: #ffebee;
    color: #c62828;
    
    .counter-icon {
      font-weight: bold;
    }
  }
  
  .counter-icon {
    font-size: 14px;
    margin-right: 2px;
  }
  
  .counter-text {
    font-size: 14px;
    font-weight: 400;
  }
}

.modal-footer {
  padding: 16px;
}

.generating-phase {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
}

.spinner {
  position: relative;
  width: 60px;
  height: 60px;
  margin-bottom: 16px;
  
  span {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #255a80;
    animation: rotate 2s linear infinite both;
  }

  span:nth-of-type(1) {
    animation-delay: 0.2s;
  }

  span:nth-of-type(2) {
    animation: rotate 2s 0.4s linear infinite both;
  }

  span:nth-of-type(3) {
    animation: rotate 2s 0.6s linear infinite both;
  }

  span:nth-of-type(4) {
    animation-delay: 0.8s;
  }

  span:nth-of-type(5) {
    animation-delay: 1s;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg) translateY(-200%) rotate(0deg) scale(0.2);
  }
  50% {
    transform: rotate(360deg) translateY(-200%) rotate(-360deg) scale(1);
  }
  to {
    transform: rotate(360deg) translateY(-200%) rotate(-360deg) scale(0);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}
</style>
